import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config/config';

// Async thunks
export const fetchProjectPermits = createAsyncThunk(
  'projectPermits/fetchProjectPermits',
  async (projectId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/projects/${projectId}/permits`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch project permits');
      }
      
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch project permits');
    }
  }
);

export const savePermitForm = createAsyncThunk(
  'projectPermits/savePermitForm',
  async ({ projectId, formType, formData }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/projects/${projectId}/permits`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          formType,
          formData
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to save permit form');
      }
      
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to save permit form');
    }
  }
);

export const updatePermitForm = createAsyncThunk(
  'projectPermits/updatePermitForm',
  async ({ projectId, permitId, formData }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/projects/${projectId}/permits/${permitId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to update permit form');
      }
      
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update permit form');
    }
  }
);

export const deletePermitForm = createAsyncThunk(
  'projectPermits/deletePermitForm',
  async ({ projectId, permitId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/projects/${projectId}/permits/${permitId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        return rejectWithValue(data.message || 'Failed to delete permit form');
      }
      
      return permitId;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete permit form');
    }
  }
);

const initialState = {
  permits: [],
  currentPermit: null,
  loading: false,
  error: null,
  formTypes: [
    { id: 'bidNotes', name: 'Bid Notes', available: true },
    { id: 'permitRequestForm', name: 'Permit Request Form', available: true },
    { id: 'preliminaryInfo', name: 'Preliminary Information', available: true },
    { id: 'projectChecklist', name: 'Project Checklist', available: true },
    { id: 'dailyChecklist', name: 'Daily Checklist', available: true },
    { id: 'warrantyForm', name: 'Warranty Certificate', available: true },
    { id: 'tileForm', name: 'Tile', available: true },
    { id: 'slateForm', name: 'Slate', available: true },
    { id: 'singlePly', name: 'Single Ply', available: true },
    { id: 'compositionShingle', name: 'Composition Shingle', available: true },
    { id: 'prepDryIn', name: 'Prep and Dry In', available: true },
    { id: 'peelAndStick', name: 'Peel and Stick', available: true },
    { id: 'builtUpRoofMaterial', name: 'Built Up Roof Material', available: true },
    { id: 'alxMaterial', name: 'ALX Material', available: true },
    { id: 'lumber', name: 'Lumber', available: true },
    { id: 'flashings', name: 'Flashings', available: true },
    { id: 'metalRoofFlashing', name: 'Metal Roof Flashing', available: true },
    { id: 'rfq1', name: 'RFQ1', available: false },
    { id: 'roofPanelCutList', name: 'Roof Panel Cut List', available: true }
  ]
};

const projectPermitsSlice = createSlice({
  name: 'projectPermits',
  initialState,
  reducers: {
    setCurrentPermit: (state, action) => {
      state.currentPermit = action.payload;
    },
    clearCurrentPermit: (state) => {
      state.currentPermit = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch project permits
      .addCase(fetchProjectPermits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectPermits.fulfilled, (state, action) => {
        state.loading = false;
        state.permits = action.payload;
      })
      .addCase(fetchProjectPermits.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Save permit form
      .addCase(savePermitForm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(savePermitForm.fulfilled, (state, action) => {
        state.loading = false;
        state.permits.push(action.payload);
        state.currentPermit = action.payload;
      })
      .addCase(savePermitForm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update permit form
      .addCase(updatePermitForm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePermitForm.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.permits.findIndex(permit => permit._id === action.payload._id);
        if (index !== -1) {
          state.permits[index] = action.payload;
        }
        state.currentPermit = action.payload;
      })
      .addCase(updatePermitForm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete permit form
      .addCase(deletePermitForm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePermitForm.fulfilled, (state, action) => {
        state.loading = false;
        state.permits = state.permits.filter(permit => permit._id !== action.payload);
        if (state.currentPermit && state.currentPermit._id === action.payload) {
          state.currentPermit = null;
        }
      })
      .addCase(deletePermitForm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { setCurrentPermit, clearCurrentPermit } = projectPermitsSlice.actions;

export default projectPermitsSlice.reducer;
